import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalData } from "../hooks/useLocalData";
import { useAuth } from "../hooks/useAuth";
import { generateNoteFromTranscript } from "../services/aiService";
import {
  ArrowLeft,
  FileText,
  Play,
  Pause,
  Download,
  Mic,
  Trash2,
  RefreshCw,
  CheckCircle,
  Shield,
  AlertTriangle,
} from "lucide-react";

const AUDIO_DB = "FieldNotesAudio";
const AUDIO_DB_VERSION = 1;

function openAudioDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(AUDIO_DB, AUDIO_DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("chunks")) {
        db.createObjectStore("chunks", { autoIncrement: true });
      }
      if (!db.objectStoreNames.contains("recordings")) {
        db.createObjectStore("recordings", { keyPath: "sessionId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function loadAllRecordings() {
  const db = await openAudioDB();

  const recordingsRows = await new Promise((resolve) => {
    try {
      const tx = db.transaction("recordings", "readonly");
      const req = tx.objectStore("recordings").getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });

  const chunkRows = await new Promise((resolve) => {
    try {
      const tx = db.transaction("chunks", "readonly");
      const req = tx.objectStore("chunks").getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });

  const chunksBySession = {};
  for (const row of chunkRows) {
    if (!row || !row.sessionId) continue;
    if (!chunksBySession[row.sessionId]) chunksBySession[row.sessionId] = [];
    chunksBySession[row.sessionId].push(row);
  }

  const known = new Set(recordingsRows.map((r) => r.sessionId));

  const results = recordingsRows.map((r) => ({
    sessionId: r.sessionId,
    timestamp: r.timestamp || 0,
    audio: r.audio || null,
    transcript: r.transcript || [],
    flaggedSegments: r.flaggedSegments || [],
    duration: r.duration || 0,
    source: "assembled",
    chunkCount: chunksBySession[r.sessionId]?.length || 0,
  }));

  for (const [sessionId, chunks] of Object.entries(chunksBySession)) {
    if (known.has(sessionId)) continue;
    chunks.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    const earliest = chunks[0]?.timestamp || 0;
    const parts = chunks.map((c) => c.data).filter(Boolean);
    const blob = parts.length > 0 ? new Blob(parts, { type: "audio/webm" }) : null;

    results.push({
      sessionId,
      timestamp: earliest,
      audio: blob,
      transcript: [],
      flaggedSegments: [],
      duration: 0,
      source: "chunks",
      chunkCount: chunks.length,
    });
  }

  results.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  return results;
}

async function deleteRecordingFromDB(sessionId) {
  const db = await openAudioDB();

  await new Promise((resolve) => {
    try {
      const tx = db.transaction("recordings", "readwrite");
      tx.objectStore("recordings").delete(sessionId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });

  await new Promise((resolve) => {
    try {
      const tx = db.transaction("chunks", "readwrite");
      const store = tx.objectStore("chunks");
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) {
          if (cursor.value?.sessionId === sessionId) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

function transcriptToText(transcript) {
  if (!transcript) return "";
  if (typeof transcript === "string") return transcript;
  if (Array.isArray(transcript)) {
    return transcript.map((e) => (typeof e === "string" ? e : e?.text || "")).join(" ");
  }
  return "";
}

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function RecoveryPage() {
  const navigate = useNavigate();
  const { addNote, recordDraftSession } = useLocalData();
  const { profile } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const playUrlRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await loadAllRecordings();
      setRecordings(all);
    } catch (e) {
      setError(e.message || "Failed to read local audio store");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch {}
      }
      if (playUrlRef.current) {
        try { URL.revokeObjectURL(playUrlRef.current); } catch {}
      }
    };
  }, [load]);

  const handlePlay = (recording) => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
    }
    if (playUrlRef.current) {
      try { URL.revokeObjectURL(playUrlRef.current); } catch {}
      playUrlRef.current = null;
    }
    if (!recording.audio) return;

    const url = URL.createObjectURL(recording.audio);
    playUrlRef.current = url;

    const audio = new Audio(url);
    audioRef.current = audio;
    audio
      .play()
      .then(() => setPlayingId(recording.sessionId))
      .catch((e) => setError("Could not play audio: " + e.message));
    audio.onended = () => setPlayingId(null);
    audio.onpause = () => setPlayingId(null);
  };

  const handleStop = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
    }
    setPlayingId(null);
  };

  const handleDownload = (recording) => {
    if (!recording.audio) return;
    const url = URL.createObjectURL(recording.audio);
    const a = document.createElement("a");
    a.href = url;
    const ts = recording.timestamp
      ? new Date(recording.timestamp).toISOString().replace(/[:.]/g, "-")
      : "unknown";
    a.download = `fieldnotes-${recording.sessionId}-${ts}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleGenerate = async (recording, individualName, pastedTranscript) => {
    const fromStored = transcriptToText(recording.transcript);
    const transcriptText = (pastedTranscript || fromStored || "").trim();

    if (transcriptText.length < 10) {
      setError(
        "No transcript available. Either paste a transcript below, or download the audio and dictate a transcript into the box."
      );
      return;
    }

    setGeneratingId(recording.sessionId);
    setError(null);

    try {
      const result = await generateNoteFromTranscript(transcriptText, {
        individualName: individualName?.trim() || "Recovered — Unknown",
        date: new Date(recording.timestamp || Date.now()).toLocaleDateString(),
        locationType: "Out-of-Clinic",
        location: "Community",
        clinician: profile?.displayName
          ? `${profile.displayName}${profile.credentials ? `, ${profile.credentials}` : ""}${profile.employeeId ? ` (${profile.employeeId})` : ""}`
          : "ICM Staff",
        serviceType: "ICM Weekly Visit",
        duration: recording.duration || 1800,
        flaggedNotes: (recording.flaggedSegments || []).map((seg) => ({
          time:
            Math.floor((seg.startTime || 0) / 60) +
            ":" +
            String(Math.floor((seg.startTime || 0) % 60)).padStart(2, "0"),
          text: seg.transcript || "",
        })),
      });

      setGeneratingId(null);

      if (result.success) {
        navigate("/session/review", {
          state: {
            sessionId: recording.sessionId,
            note: result.note,
            fromAI: true,
            duration: recording.duration || 1800,
            individualName: individualName?.trim() || "Recovered — Unknown",
            transcript: transcriptText,
          },
        });
      } else {
        setError("Generation failed: " + (result.error || "Unknown error"));
      }
    } catch (e) {
      setGeneratingId(null);
      setError("Generation error: " + (e.message || "Unknown error"));
    }
  };

  const handleSaveDraft = (recording, individualName, pastedTranscript) => {
    const fromStored = transcriptToText(recording.transcript);
    const transcriptText = (pastedTranscript || fromStored || "").trim();
    const duration = recording.duration || 1800;
    const durationMin = Math.round(duration / 60);
    const billingUnits = Math.max(1, Math.ceil(durationMin / 15));

    addNote({
      individualName: individualName?.trim() || "Recovered — Unknown",
      date: new Date(recording.timestamp || Date.now()).toLocaleDateString(),
      serviceType: "ICM Weekly Visit",
      location: "Community",
      locationType: "Out-of-Clinic",
      status: "draft",
      billingCode: "H0039",
      billingUnits: `${billingUnits} units`,
      duration: `${durationMin} min`,
      durationSeconds: duration,
      confidence: null,
      hasPriorityFlags: false,
      noteData: null,
      transcript: transcriptText,
      flaggedNotes: recording.flaggedSegments || [],
      recoveredFromSessionId: recording.sessionId,
      recoveredAt: new Date().toISOString(),
    });

    recordDraftSession({
      individualName: individualName?.trim() || "Recovered Session",
      duration,
    });

    setError(null);
    navigate("/notes");
  };

  const handleDelete = async (recording) => {
    const confirmed = window.confirm(
      `Delete the recording for ${new Date(recording.timestamp || Date.now()).toLocaleString()}? The audio cannot be recovered after this.`
    );
    if (!confirmed) return;
    await deleteRecordingFromDB(recording.sessionId);
    load();
  };

  return (
    <div className="min-h-screen bg-surface-primary">
      <header
        className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, #0f1729, #162033)",
          borderBottom: "1px solid rgba(99,179,237,0.1)",
        }}
      >
        <button
          className="flex items-center gap-3"
          onClick={() => navigate("/settings")}
        >
          <ArrowLeft size={20} className="text-gray-300" />
          <div>
            <div className="text-lg font-bold text-gray-100">Session Recovery</div>
            <div className="text-[11px] text-gray-500">
              Audio still stored on this device
            </div>
          </div>
        </button>
        <button
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: "rgba(99,179,237,0.1)" }}
          onClick={load}
          title="Refresh"
        >
          <RefreshCw size={16} className="text-blue-400" />
        </button>
      </header>

      <div className="px-5 py-4">
        <div
          className="rounded-xl p-4 mb-4"
          style={{
            background: "rgba(99,179,237,0.04)",
            border: "1px solid rgba(99,179,237,0.1)",
          }}
        >
          <div className="flex items-start gap-2">
            <Shield size={14} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-blue-400 mb-1">
                Recover Lost Sessions
              </div>
              <div className="text-[11px] text-gray-500 leading-relaxed">
                These are audio recordings stored in the browser on this device.
                They were captured during sessions but never made it through to a
                submitted note. Generate a note from one if a transcript was
                captured, or download the audio as a backup. Delete only when
                you're certain you no longer need it.
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            className="rounded-xl p-3 mb-4 flex items-start gap-2"
            style={{
              background: "rgba(252,129,129,0.06)",
              border: "1px solid rgba(252,129,129,0.2)",
            }}
          >
            <AlertTriangle size={14} className="text-red-400 mt-0.5 shrink-0" />
            <div className="text-xs text-red-300 leading-relaxed">{error}</div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-500 py-10 text-sm">Loading…</div>
        ) : recordings.length === 0 ? (
          <div className="text-center py-10">
            <CheckCircle size={36} className="text-green-400 mx-auto mb-3" />
            <div className="text-sm text-gray-400 font-semibold">
              No orphan recordings
            </div>
            <div className="text-[11px] text-gray-600 mt-1">
              Every recording on this device has been processed.
            </div>
          </div>
        ) : (
          recordings.map((r) => (
            <RecoveryCard
              key={r.sessionId}
              recording={r}
              playing={playingId === r.sessionId}
              generating={generatingId === r.sessionId}
              onPlay={() => handlePlay(r)}
              onStop={handleStop}
              onDownload={() => handleDownload(r)}
              onGenerate={(name, pasted) => handleGenerate(r, name, pasted)}
              onSaveDraft={(name, pasted) => handleSaveDraft(r, name, pasted)}
              onDelete={() => handleDelete(r)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function RecoveryCard({
  recording,
  playing,
  generating,
  onPlay,
  onStop,
  onDownload,
  onGenerate,
  onSaveDraft,
  onDelete,
}) {
  const [name, setName] = useState("");
  const [pastedTranscript, setPastedTranscript] = useState("");
  const [showPaste, setShowPaste] = useState(false);

  const fromStored = transcriptToText(recording.transcript);
  const transcriptPreview = fromStored.slice(0, 240);
  const wordCount = fromStored.split(/\s+/).filter(Boolean).length;
  const sizeBytes = recording.audio?.size || 0;
  const dateStr = recording.timestamp
    ? new Date(recording.timestamp).toLocaleString()
    : "Unknown time";

  const hasTranscript = fromStored.length > 10 || pastedTranscript.length > 10;
  const finalTranscriptText = pastedTranscript.trim() || fromStored;

  return (
    <div
      className="rounded-xl p-4 mb-3"
      style={{
        background: "rgba(15,23,42,0.5)",
        border: "1px solid rgba(99,179,237,0.08)",
      }}
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="text-sm font-semibold text-gray-200">{dateStr}</div>
          <div className="text-[10px] text-gray-600 mt-0.5 font-mono">
            {recording.sessionId}
          </div>
        </div>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider"
          style={{
            background:
              recording.source === "assembled"
                ? "rgba(72,187,120,0.1)"
                : "rgba(236,201,75,0.1)",
            color: recording.source === "assembled" ? "#68d391" : "#ecc94b",
          }}
        >
          {recording.source === "assembled" ? "COMPLETE" : "RAW CHUNKS"}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3 mb-3">
        <div>
          <div className="text-[9px] text-gray-600 uppercase tracking-wide">
            Duration
          </div>
          <div className="text-xs text-gray-300 font-semibold mt-0.5">
            {formatDuration(recording.duration)}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-gray-600 uppercase tracking-wide">
            Audio Size
          </div>
          <div className="text-xs text-gray-300 font-semibold mt-0.5">
            {formatBytes(sizeBytes)}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-gray-600 uppercase tracking-wide">
            Transcript
          </div>
          <div className="text-xs text-gray-300 font-semibold mt-0.5">
            {wordCount > 0 ? `${wordCount} words` : "None"}
          </div>
        </div>
      </div>

      {transcriptPreview && (
        <div
          className="rounded-lg p-2 mb-3 text-[11px] text-gray-400 leading-relaxed italic"
          style={{ background: "rgba(15,23,42,0.6)" }}
        >
          "{transcriptPreview}
          {fromStored.length > 240 ? "…" : ""}"
        </div>
      )}

      <input
        type="text"
        placeholder="Individual name (so it shows up correctly in Notes)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-lg p-2 text-xs text-gray-300 mb-2"
        style={{
          background: "rgba(15,23,42,0.6)",
          border: "1px solid rgba(99,179,237,0.1)",
          outline: "none",
        }}
      />

      {showPaste && (
        <textarea
          placeholder="Paste or type a transcript here (use this if the auto-transcript is empty)"
          value={pastedTranscript}
          onChange={(e) => setPastedTranscript(e.target.value)}
          className="w-full rounded-lg p-2 text-xs text-gray-300 mb-2 resize-none"
          style={{
            background: "rgba(15,23,42,0.6)",
            border: "1px solid rgba(236,201,75,0.2)",
            outline: "none",
            minHeight: 100,
          }}
        />
      )}

      <div className="flex gap-2 flex-wrap">
        {recording.audio && (
          <button
            className="py-2 px-3 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1.5"
            style={{
              background: "rgba(99,179,237,0.08)",
              color: "#63b3ed",
              border: "1px solid rgba(99,179,237,0.2)",
            }}
            onClick={playing ? onStop : onPlay}
          >
            {playing ? <Pause size={12} /> : <Play size={12} />}{" "}
            {playing ? "Stop" : "Play"}
          </button>
        )}
        {recording.audio && (
          <button
            className="py-2 px-3 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1.5"
            style={{
              background: "rgba(79,209,197,0.08)",
              color: "#4fd1c5",
              border: "1px solid rgba(79,209,197,0.2)",
            }}
            onClick={onDownload}
          >
            <Download size={12} /> Download
          </button>
        )}
        <button
          className="py-2 px-3 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1.5"
          style={{
            background: "rgba(236,201,75,0.06)",
            color: "#ecc94b",
            border: "1px solid rgba(236,201,75,0.15)",
          }}
          onClick={() => setShowPaste(!showPaste)}
        >
          {showPaste ? "Hide Paste" : "Paste Transcript"}
        </button>
        <button
          className="flex-1 py-2 px-3 rounded-lg text-[11px] font-semibold text-white flex items-center justify-center gap-1.5"
          style={{
            background: "linear-gradient(135deg, #38a169, #2f855a)",
            opacity: generating || !hasTranscript ? 0.5 : 1,
            minWidth: 120,
          }}
          onClick={() => onGenerate(name, pastedTranscript)}
          disabled={generating || !hasTranscript}
        >
          <FileText size={12} />{" "}
          {generating ? "Generating…" : "Generate Note"}
        </button>
        <button
          className="py-2 px-3 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1.5"
          style={{
            background: "rgba(99,179,237,0.06)",
            color: "#63b3ed",
            border: "1px solid rgba(99,179,237,0.15)",
          }}
          onClick={() => onSaveDraft(name, pastedTranscript)}
        >
          Save Draft
        </button>
        <button
          className="py-2 px-3 rounded-lg text-[11px] font-semibold flex items-center justify-center"
          style={{
            background: "rgba(220,38,38,0.06)",
            color: "#fc8181",
            border: "1px solid rgba(220,38,38,0.15)",
          }}
          onClick={onDelete}
          title="Delete from device"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}
